import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";

import { ProgressBar } from "../components/ProgressBar";
import { CartIconWithItemsButton } from "../components/cart/CartIconWithItemsButton";
import { StoryAvatar } from "../components/story/StoryAvatar";
import { Item } from "../components/story/StoryItem";
import { StoryPlayPause } from "../components/story/StoryPlayPause";
import { StorySection } from "../components/story/StorySection";
import { useIsomorphicLayoutEffect } from "../hooks/use-isomorphic-layout-effect";
import type { SectionWithCheckoutProducts } from "../hooks/use-items-with-checkout";
import type { LookbookProps } from "../lookbook";
import { SizeContext } from "../providers/Size";

export interface StoryViewProps extends LookbookProps {
  playing: boolean;
  setPlaying: (playing: boolean) => void;
  sections: SectionWithCheckoutProducts[];
}

export function StoryView(props: StoryViewProps) {
  const {
    brandName,
    lookbook,
    favicon,
    sections,
    fullPath,
    showBranding,
    meta,
    playing,
    setPlaying,
    checkout,
  } = props;

  const router = useRouter();

  const { size } = useContext(SizeContext);

  const [currentSectionOrItemId, setCurrentSectionOrItemId] = useState(
    props.currentSectionOrItemId,
  );

  const [longPressing, setLongPressing] = useState(false);

  useIsomorphicLayoutEffect(() => {
    setCurrentSectionOrItemId(props.currentSectionOrItemId);
  }, [props.currentSectionOrItemId]);

  useEffect(() => {
    if (Array.isArray(router.query.slug)) {
      const [, , id] = router.query.slug;
      if (id) {
        setCurrentSectionOrItemId(id);
      }
    }
  }, [router.query.slug]);

  const messageId = router.query.message_id;

  const storySlides = sections.flatMap((section) => [
    section,
    ...section.items,
  ]);

  const currentIdx = storySlides.findIndex(
    (s) => s.id === currentSectionOrItemId,
  );
  const currentSlide = storySlides[currentIdx] ?? storySlides[0];
  const nextSlide = storySlides[currentIdx + 1];
  const prevSlide = storySlides[currentIdx - 1];
  const nextHref = nextSlide ? `${fullPath}/${nextSlide.id}` : undefined;
  const prevHref = prevSlide ? `${fullPath}/${prevSlide.id}` : undefined;
  const restartHref = storySlides[0]
    ? `${fullPath}/${storySlides[0].id}`
    : fullPath;

  const onNext = nextHref
    ? () => {
        router.replace(
          { pathname: nextHref, query: { message_id: messageId } },
          undefined,
          { shallow: true },
        );
      }
    : undefined;
  const onPrevious = prevHref
    ? () => {
        router.replace(
          { pathname: prevHref, query: { message_id: messageId } },
          undefined,
          { shallow: true },
        );
      }
    : undefined;
  const onRestart = () => {
    router.replace(
      { pathname: restartHref, query: { message_id: messageId } },
      undefined,
      { shallow: true },
    );
  };

  const avatar = (
    <StoryAvatar imageSrcUrl={favicon.src} brandName={meta.siteName} />
  );

  return (
    <div
      className="story-view"
      style={{ width: size?.width, height: size?.height }}>
      {currentSlide?.__typename === "LookbookItem" ? (
        <div className="lookbook--story--progress-play-pause">
          <ProgressBar max={storySlides.length} value={currentIdx + 1} />
          {checkout.enabled ? (
            <CartIconWithItemsButton onPause={() => setPlaying?.(false)} />
          ) : null}
          <StoryPlayPause
            secondsPerSlide={props.secondsPerSlide}
            nextHref={nextHref ?? null}
            playing={playing ?? false}
            longPressing={longPressing}
            isLastSlide={!nextSlide}
            onPlay={() => setPlaying?.(true)}
            onPause={() => setPlaying?.(false)}
            onAdvance={onNext}
            onRestart={onRestart}
          />
        </div>
      ) : null}
      {storySlides.map((slide) => {
        const isCurrent = slide.id === currentSlide?.id;
        const isNext = slide.id === nextSlide?.id;
        const isPrevious = slide.id === prevSlide?.id;

        switch (slide.__typename) {
          case "LookbookSection": {
            return (
              <StorySection
                key={slide.id}
                section={slide}
                firstItem={slide.items[0]}
                lookbookTitle={lookbook.title}
                showBranding={showBranding}
                brandName={brandName}
                avatar={avatar}
                position={currentIdx}
                isCurrent={isCurrent}
                isPrevious={isPrevious}
                isNext={isNext}
                onPrevious={onPrevious}
                onNext={
                  onNext
                    ? () => {
                        setPlaying?.(true);
                        onNext();
                      }
                    : undefined
                }
              />
            );
          }
          case "LookbookItem": {
            return (
              <Item
                key={slide.id}
                item={slide}
                position={currentIdx}
                isCurrent={isCurrent}
                isNext={isNext}
                isPrevious={isPrevious}
                onPause={() => setPlaying?.(false)}
                onPrevious={onPrevious}
                onNext={onNext}
                onLongPress={() => setLongPressing(true)}
                onLongPressReleased={() => setLongPressing(false)}
                checkout={checkout}
              />
            );
          }
        }
      })}
    </div>
  );
}
